import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { IssueReportSource } from '../../redux/features/issueReport/issueReport.props';
import {
  selectIsIssueReportingEnabled,
  selectIssueReportingVariant,
  selectIssueTypes,
} from '../../redux/features/config/config.selector';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectIssueReportTotalCount,
  selectIssueReports,
  selectStagedIssueType,
  selectUnclassifiedIssueReportCount,
} from '../../redux/features/issueReport/issueReport.selectors';
import {
  clearStagedIssueType,
  closeFlagWindow,
  commitIssueType,
  flagIssue,
  stageIssueType,
  voidIssue,
} from '../../redux/features/issueReport/issueReport.slice';

/**
 * HITL-1492 Pillar 3, Part A — the single seam HITL-1499/1500/1501 consume.
 *
 * Unused in this ticket by design. It exists here so those three tickets can be built
 * in parallel against one agreed shape rather than each inventing its own dispatch
 * calls and drifting.
 *
 * The flag gate lives here, ONCE, so no caller can leak a network call with the
 * feature off.
 */
const useIssueReport = () => {
  const dispatch = useAppDispatch();

  const isEnabled = useSelector(selectIsIssueReportingEnabled);
  const issueTypes = useSelector(selectIssueTypes);
  const variant = useSelector(selectIssueReportingVariant);

  const reports = useSelector(selectIssueReports);
  const activeReport = useSelector(selectActiveIssueReport);
  const activeWindowExpiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const stagedType = useSelector(selectStagedIssueType);
  const unclassifiedCount = useSelector(selectUnclassifiedIssueReportCount);
  const totalCount = useSelector(selectIssueReportTotalCount);

  /** Returns the new `issue_id`, so HITL-1499's toast can address its own record. */
  const flag = useCallback(
    (args: { source?: IssueReportSource } = {}) => {
      if (!isEnabled) return null;
      return dispatch(flagIssue(args));
    },
    [isEnabled, dispatch]
  );

  const commit = useCallback(
    (args: {
      issue_id: string;
      issue_type: string | null;
      issue_detail?: string | null;
    }) => {
      if (!isEnabled) return null;
      return dispatch(commitIssueType(args));
    },
    [isEnabled, dispatch]
  );

  const voidReport = useCallback(
    (issueId: string) => {
      if (!isEnabled) return null;
      return dispatch(voidIssue({ issue_id: issueId }));
    },
    [isEnabled, dispatch]
  );

  const stage = useCallback(
    (issueType: string) => {
      if (!isEnabled) return;
      dispatch(stageIssueType({ issue_type: issueType }));
    },
    [isEnabled, dispatch]
  );

  const clearStaged = useCallback(() => {
    if (!isEnabled) return;
    dispatch(clearStagedIssueType());
  }, [isEnabled, dispatch]);

  const closeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(closeFlagWindow());
  }, [isEnabled, dispatch]);

  return {
    isEnabled,
    issueTypes,
    variant,
    reports,
    activeReport,
    activeWindowExpiresAt,
    stagedType,
    unclassifiedCount,
    totalCount,
    flagIssue: flag,
    commitIssueType: commit,
    voidIssue: voidReport,
    stageIssueType: stage,
    clearStagedIssueType: clearStaged,
    closeFlagWindow: closeWindow,
  };
};

export default useIssueReport;
